import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

interface DeleteSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionName: string;
  isWorkSpace: boolean;
  onConfirm: () => void | Promise<void>;
}

export const DeleteSessionModal: React.FC<DeleteSessionModalProps> = ({
  isOpen,
  onClose,
  sessionName,
  onConfirm
}) => {
  const { t } = useTranslation('common');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to delete session:', err);
      const message =
        err instanceof Error ? err.message : t('unknownError');
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sessions.deleteTitle')}
      confirmLabel={t('common.delete')}
      confirmIcon={<Trash2 size={16} />}
      isConfirming={isDeleting}
      onConfirm={handleDelete}
      error={error}
    >
      <div className="bg-rose-500/5 border border-rose-500/10 rounded-2xl p-4 flex items-start gap-4">
        <AlertTriangle className="text-rose-500 shrink-0 mt-0.5" size={18} />
        <div className="space-y-1">
          <p className="text-sm font-bold text-rose-500/90">
            {t('sessions.deleteWarning')}
          </p>
          <p className="text-xs text-muted-foreground font-medium">
            {t('sessions.deleteDescription')}{' '}
            <span className="text-foreground font-bold italic">"{sessionName}"</span>?
          </p>
        </div>
      </div>
    </ConfirmModal>
  );
};
